import {
    ChangeDetectionStrategy,
    Component,
    DoCheck,
    forwardRef,
    inject,
    Input,
    IterableDiffer,
    IterableDiffers,
    OnChanges,
    OnDestroy,
    SimpleChanges
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { isObservable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FilterListComponent)
        }
    ],
    selector: "rask-filter-list",
    styleUrls: ["./filter-list.component.scss"],
    templateUrl: "./filter-list.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule]
})
export class FilterListComponent implements ControlValueAccessor, OnChanges, OnDestroy, DoCheck {
    @Input() aid: string;
    currentItems: any[];
    disabled: boolean;
    @Input() exclude = [] as any[];
    excludeDiffer: IterableDiffer<any> | null;
    @Input() excludeFilterProperty?: string;
    filteredItems: any[];
    @Input() items: any;
    itemsDiffer: IterableDiffer<any> | null;

    searchString: string;
    @Input() size = 5;
    value: any;

    private readonly destroy$ = new Subject<void>();
    private readonly iterableDiffers = inject(IterableDiffers);

    private selectedPrivate = [];

    onChange: any = () => {
        // Metoden måste finnas, även om den är tom, för att resterande kod ska lira som önskat
    };
    onTouched: any = () => {
        // Metoden måste finnas, även om den är tom, för att resterande kod ska lira som önskat
    };

    get selected() {
        return this.selectedPrivate;
    }

    set selected(val) {
        this.selectedPrivate = val;
        this.onChange(this.selectedPrivate);
        this.onTouched();
    }

    @Input() formatter = (o: any): string => o;

    @Input() filter = (str: string, items: any[]) => items.filter(i => str ? this.formatter(i).toLocaleLowerCase().indexOf(str) > -1 : true);

    update = () => {
        this.filteredItems = this.filter(this.searchString, this.currentItems)
            .filter(fi => this.excludeFilter(this.exclude, fi));
        this.onChange();
    };

    keyUp = (event: KeyboardEvent) => {
        this.update();
    };

    ngDoCheck() {
        if (!!this.itemsDiffer && this.itemsDiffer.diff(this.items)) {
            this.update();
        }

        if (!!this.excludeDiffer && this.excludeDiffer.diff(this.exclude)) {
            this.update();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.items) {
            this.setupItems();
        }
        if (changes.exclude) {
            this.setupExcludes();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    select(): void {
        this.selected = this.value;
    }

    setDisabledState = (isDisabled: boolean) => this.disabled = isDisabled;

    setupExcludes() {
        this.excludeDiffer = this.iterableDiffers.find(this.exclude).create();
    }

    setupItems() {
        if (isObservable(this.items)) {
            this.items.pipe(
                takeUntil(this.destroy$)
            ).subscribe(i => {
                this.currentItems = i as any[];
                this.update();
            });
        } else {
            this.currentItems = this.items;
            this.itemsDiffer = this.iterableDiffers.find(this.items).create();
        }
    }

    writeValue(obj: any): void {
        if (obj !== undefined) {
            this.selectedPrivate = obj;
            this.value = obj;
        }
    }

    private readonly excludeFilter = (excludeItems, item): boolean => this.excludeFilterProperty ?
        !excludeItems.some(ei => ei[this.excludeFilterProperty] === item[this.excludeFilterProperty]) :
        excludeItems.indexOf(item) === -1;
}
